"use client";

import { Box, Group, Stack, Text } from "@mantine/core";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { FiMinus } from "react-icons/fi";

import classes from "./_css/ApplicationRestrictionCard.module.css";

export type ApplicationRestrictionBodyKind =
  | "pointsAtOrBelowZero"
  | "policySuspension";

type Props = {
  suspensionStartDateAt: string | null;
  suspensionEndDateAt: string | null;
  bodyKind?: ApplicationRestrictionBodyKind;
};

function isIndefiniteEnd(dateStr: string): boolean {
  return parseISO(dateStr).getFullYear() >= 9999;
}

/** モバイル ApplicationRestrictionCard と同じ（ゼロ埋めなし） */
function formatDate(dateStr: string): string {
  const d = parseISO(dateStr);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

function periodLabel(start: string | null, end: string | null): string | null {
  const hasIndefiniteEnd = end != null && isIndefiniteEnd(end);
  if (start != null && (end == null || hasIndefiniteEnd)) {
    return `${formatDate(start)}〜`;
  }
  if (start != null && end != null) {
    return `${formatDate(start)}〜${formatDate(end)}`;
  }
  if (end != null && !hasIndefiniteEnd) {
    return `〜${formatDate(end)}`;
  }
  return null;
}

function primaryBodyText(kind: ApplicationRestrictionBodyKind): string {
  if (kind === "policySuspension") {
    return "勤務実績・評価・応募制限の履歴等により応募制限となり、新規の求人への応募が制限されています。";
  }
  return "持ち点が0pt以下のため、新規の求人への応募が制限されています。";
}

function daysLeftFromEnd(end: string | null): number | null {
  if (end == null || isIndefiniteEnd(end)) return null;
  const endDay = parseISO(end);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDay.setHours(0, 0, 0, 0);
  const days = differenceInCalendarDays(endDay, today);
  return days < 0 ? 0 : days;
}

export function ApplicationRestrictionCard({
  suspensionStartDateAt,
  suspensionEndDateAt,
  bodyKind = "pointsAtOrBelowZero",
}: Props) {
  const daysLeft = daysLeftFromEnd(suspensionEndDateAt);
  const label = periodLabel(suspensionStartDateAt, suspensionEndDateAt);
  const showPeriodBox =
    suspensionStartDateAt != null ||
    suspensionEndDateAt != null ||
    daysLeft != null;

  return (
    <Box className={classes.card}>
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <span className={classes.iconCircle} aria-hidden="true">
            <FiMinus size={18} color="white" />
          </span>
          <Text className={classes.title}>応募制限中</Text>
        </Group>

        {showPeriodBox ? (
          <Box className={classes.periodBox}>
            <Group justify="space-between" align="center" mb={4}>
              <Text className={classes.periodLabel}>制限期間</Text>
              {daysLeft != null ? (
                <Text className={classes.daysLeft}>あと{daysLeft}日</Text>
              ) : null}
            </Group>
            {label ? (
              <Text className={classes.periodDates}>{label}</Text>
            ) : null}
          </Box>
        ) : null}

        <Stack gap={4}>
          <Text className={classes.body}>{primaryBodyText(bodyKind)}</Text>
          <Text className={classes.body}>
            ワーカー限定公開の求人のみ応募可能です。
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}
